import { Coordinates } from '@portal/models/coordinates';
import { DisputeReasonResponse } from '@portal/models/disputeReasonResponse';
import { Item } from '@portal/models/item';
import { OrderResponse } from '@portal/models/orderResponse';
import { PageContentBlockV2Shared } from '@portal/models/pageContentBlockV2Shared';
import { PromocodePaginationRequest } from '@portal/models/promocodePaginationRequest';
import { RoleSaveRequest } from '@portal/models/roleSaveRequest';
import { SkuResponse } from '@portal/models/skuResponse';
import ProductTypeEnum = Item.ProductTypeEnum;
import StatusEnum = OrderResponse.StatusEnum;
import ElementTypeEnum = PageContentBlockV2Shared.ElementTypeEnum;
import LangEnum = PromocodePaginationRequest.LangEnum;
import ProfileTypeEnum = RoleSaveRequest.ProfileTypeEnum;
import CurrencyEnum = SkuResponse.CurrencyEnum;
import OrderTypeEnum = DisputeReasonResponse.OrderTypeEnum;

export enum SignUpProfileType {
  LEGAL_SELLER = 'LEGAL_SELLER',
  LEGAL_BUYER = 'LEGAL_BUYER',
  COURIER = 'COURIER',
  PROCESSOR = 'PROCESSOR',
}

export enum AppType {
  Gdesemena = 'Gdesemena',
  Safal = 'Safal',
  Stc = 'STC',
}

export enum LanguageLocale {
  en = 'en',
  ru = 'ru',
  fr = 'fr',
  ar = 'ar',
  hi = 'hi',
  khr = 'km',
  cny = 'zh',
}

export enum Theme {
  DEFAULT = 'theme-default',
  STC = 'theme-stc',
  PHEMA = 'theme-phema',
  LILAC = 'theme-lilac',
  COMMON = 'theme-common',
}

export interface LogoConfig {
  normal: string;
  light?: string;
  small?: string;
}

export interface BuilderEnvironment extends CommonBuilderSettings {
  appType: AppType;
  locale: LanguageLocale;
  assets: AssetsBuilderSettings;
  recaptcha?: string;
  theme?: Theme;
}

export enum RegistrationType {
  EMAIL = 'EMAIL',
  PHONE = 'PHONE',
}

export enum SuggestionsProvider {
  DADDATA = 'dadata',
}

export interface SuggestionSettings {
  type: SuggestionsProvider;
  key: string;
}

export interface BuilderSettings extends CommonBuilderSettings {
  name?: string;
  id?: number;
}

export interface PasswordSettings {
  pattern: string;
  message: Record<LangEnum, string>;
  jsFlags?: string;
}

interface AssetsBuilderSettings {
  logo: {
    sidebar: LogoConfig;
    authPage: LogoConfig;
  };
  authBackground?: string;
}

export interface CommonBuilderSettings {
  langSetting: LangSettings;
  appName?: string;
  captchaSettings?: CaptchaSettings;
  country: string;
  currency: CurrencyEnum;
  passwordSettings?: PasswordSettings;
  currencySymbol: string;
  currencySettings: CurrencySettings[];
  phoneSettings: PhoneSettings[];
  requiredRegistrationAuthTypes?: RegistrationType[];
  systemLang: LangEnum;
  timeOut: number;
  currencyDisplayType?: CurrencyDisplayType;
  statusesMap?: Record<StatusEnum, StatusEnum[]>;
  includeDeliveryZoneAddress?: boolean;
  termsOfUseUrl?: string;
  privacyPolicyUrl?: string;
  enableMarginNegotiation?: boolean;
  allowedProductTypes?: ProductTypeEnum[];
  defaultCoordinates?: Coordinates;
  accessSettlement?: boolean;
  settlementApi?: boolean;
  accessTieredPrices?: boolean;
  onlyEmailPasswordReset?: boolean;
  allowSelfSignUp?: boolean;
  signUpInfo?: SignUpInfo;
  disableCommonLogin?: boolean;
  disabledProfiles?: ProfileTypeEnum[];
  disabledSignUpProfiles?: SignUpProfileType[];
  pageBuilderElements?: ElementTypeEnum[];
  manageManualDelivery?: boolean;
  merchantHelpUrl?: string;
  operatorHelpUrl?: string;
  allowIndividualPrices?: boolean;
  manageSellerRelatedItems?: RelatedItemPermissionLevel;
  isB2b?: boolean;
  disableSkuRemove?: boolean;
  disableSharing?: boolean;
  excludeProductConversion?: boolean;
  hideAgreedToReplace?: boolean;
  extraI18nMap?: Record<string, string>;
  useExternalMenu?: boolean;
  disableFutureProducts?: boolean;
  themeColors?: {
    primary: string;
    warn: string;
    accent: string;
  };
  multiSkusForTenderProducts?: boolean;
  disableMerchantDeliverySettings?: boolean;
  excludeCourier?: boolean;
  ignoreUserCords?: boolean;
  allowBuyerProfile?: string;
  companyTypeIdToProfileTypes?: Record<number, ProfileTypeEnum[]>;
  suggestionsProvider?: SuggestionSettings;
  innManageableBySeller?: boolean;
  isDistrictAddressRequired?: boolean;
  useCustomBackground?: boolean;
  supportedOrderTypes?: OrderTypeEnum[];
  disableUserRating?: boolean;
  disableSellerProductsLimit?: boolean;
  useLastLevelCategoriesForProduct?: boolean;
  disableRelatedItemsSettings?: boolean;
  disableCatalogSettings?: boolean;
  simplifiedPhoneMask?: boolean;
  creditAutoSignKey?: string;
}

export enum RelatedItemPermissionLevel {
  Product = 'product',
  Bulk = 'bulk',
}

export interface PhoneSettings {
  phonePrefix: number;
  phoneLength: number;
  active?: boolean;
}

export interface SignUpInfo {
  email: string;
  phone: string;
}

export interface CurrencySettings {
  currency: CurrencyEnum;
  conversionRatio: number;
  active?: boolean;
  format: string;
  currencySymbol: string;
}

export interface LangSettings {
  defaultLang: LangEnum;
  availableLangs: LangEnum[];
}

export enum CurrencyDisplayType {
  CODE = 'code',
  SYMBOL = 'symbol',
  SYMBOL_NARROW = 'symbol-narrow',
}

interface CaptchaSettings {
  enabled?: boolean;
  publicKey?: string;
  mobilePublicKey?: string;
}

export type LoginType = 'PHONE' | 'EMAIL';
export enum LoginTypeEnum {
  Phone = 'PHONE',
  Email = 'EMAIL',
}
